import { AddChip } from '@/components/ui/add-chip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { CustomField } from '@/lib/contacts/types'
import {
  customConditionField,
  defaultCustomFieldCondition,
} from '@/pages/SegmentCustomFieldConditionRows'
import type { SegmentEventField } from '@/pages/SegmentEventConditions'
import type { ConditionField, SegmentCondition } from '@/pages/SegmentRuleFields'

type Choice = [ConditionField, string, string, Array<string | number>]

function builtInChoices(
  membershipLevels: Array<{ id: number }>,
  groups: Array<{ id: number }>,
  courses: Array<{ id: number }>,
): Choice[] {
  const choices: Choice[] = [
    ['engagement_band', 'Engagement Band', 'any_of', ['Engaged']],
    ['open_balance', 'Open balance', 'greater_than', ['0.00']],
    ['overdue_balance', 'Overdue balance', 'greater_than', ['0.00']],
    ['renewal_due', 'Renewal-due window', 'in_next_days', [30]],
    ['member_since', 'Member-since range', 'between', ['', '']],
    ['lapsed_on', 'Lapsed-on window', 'in_last_days', [90]],
    ['completed_course', 'Completed Course', 'is', courses[0] ? [courses[0].id, ''] : []],
    [
      'membership_level',
      'Membership Level',
      'any_of',
      membershipLevels[0] ? [membershipLevels[0].id] : [],
    ],
    ['autopay', 'Autopay enrollment', 'is', ['on']],
    ['group_membership', 'Group membership', 'any_of', groups[0] ? [groups[0].id] : []],
    ['contact_type', 'Contact type', 'any_of', ['person']],
    ['affiliation_role', 'Affiliation role', 'any_of', ['Primary contact']],
    ['chapter_membership', 'Chapter criterion', 'is', ['any']],
    ['archived', 'Archived state', 'is', ['no']],
  ]
  const needsReference: ConditionField[] = [
    'membership_level',
    'group_membership',
    'completed_course',
  ]
  return choices.filter(([field, , , values]) => !needsReference.includes(field) || values.length)
}

/**
 * The Segment builder's one "Add condition" menu: every optional condition the
 * rule does not already use, then Custom Fields, then event conditions (which may
 * repeat, one row per set of events).
 */
export function SegmentAddConditionMenu({
  conditions,
  membershipLevels,
  groups,
  customFields,
  courses,
  eventFields,
  update,
  append,
}: {
  conditions: SegmentCondition[]
  membershipLevels: Array<{ id: number }>
  groups: Array<{ id: number }>
  customFields: CustomField[]
  courses: Array<{ id: number }>
  eventFields: SegmentEventField[]
  update: (field: ConditionField, next: SegmentCondition) => void
  append: (next: SegmentCondition) => void
}) {
  const used = (field: ConditionField) => conditions.some((condition) => condition.field === field)
  const choices = builtInChoices(membershipLevels, groups, courses).filter(
    ([field]) => !used(field),
  )
  const fields = customFields.filter((field) => !used(customConditionField(field)))

  return (
    <div className="pt-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AddChip>Add condition</AddChip>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-64 overflow-y-auto"
        >
          {choices.map(([field, label, operator, values]) => (
            <DropdownMenuItem
              key={field}
              onSelect={() => update(field, { field, operator, values })}
            >
              {label}
            </DropdownMenuItem>
          ))}
          {fields.length > 0 && (
            <DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Custom fields</DropdownMenuLabel>
              {fields.map((field) => (
                <DropdownMenuItem
                  key={field.id}
                  onSelect={() =>
                    update(customConditionField(field), defaultCustomFieldCondition(field))
                  }
                >
                  {field.label}
                  {field.active ? '' : ' (deactivated)'}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          )}
          {eventFields.length > 0 && (
            <DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Events</DropdownMenuLabel>
              {eventFields.map((field) => (
                <DropdownMenuItem
                  key={field.field}
                  onSelect={() => append({ field: field.field, operator: 'is', values: [] })}
                >
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
